import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Howl } from "howler";

const buttonClickSound = new Howl({
  src: ["/assets/audio/click-button.ogg", "/assets/audio/click-button.mp3"],
  volume: 0.2,
});

const steps = [
  {
    title: "Drag & Drop Words",
    text: "Drag words from the bank into the Venn diagram zones. Each zone represents one or more themes.",
  },
  {
    title: "Match Themes",
    text: "Place words in zones that match their themes. For example, 'Blender' belongs in 'Kitchen Tools'.",
  },
  {
    title: "Check & Lock",
    text: "Click CHECK to see if your placements are correct. Correct words will be locked in place, incorrect ones return to the bank.",
  },
  {
    title: "Solve the Puzzle",
    text: "You have 5 guesses to place all 7 words correctly. Plan carefully to solve the Venn diagram puzzle!",
  },
];

const HowToPlayModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);

  // Reset step when modal opens
  React.useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const handleNext = () => {
    if (step < steps.length - 1) {
      buttonClickSound.play();
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      buttonClickSound.play();
      setStep(step - 1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        buttonClickSound.play();
        onClose();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          border: "2px solid #8FBDC4",
          p: 0,
        },
      }}
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.18)" } }}
      transitionDuration={300}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
        <IconButton
          onClick={() => {
            buttonClickSound.play();
            onClose();
          }}
          aria-label="Close"
          size="large"
          sx={{
            minWidth: 44,
            minHeight: 44,
            p: 1,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
          title="Close"
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minWidth: { xs: 280, sm: 320 },
          minHeight: 140,
        }}
      >
        <Fade in={true} key={step} timeout={350}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <HelpOutlineIcon
              sx={{ fontSize: 36, color: "#222", mb: 0.5 }}
              aria-label="How to play"
            />
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  fontSize: { xs: "1.25rem", sm: "1.4rem" },
                  mb: 0.5,
                }}
              >
                {steps[step].title}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                mb: 1,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                color: "#444",
                lineHeight: 1.5,
              }}
            >
              {steps[step].text}
            </Typography>
            {/* Step Progress Indicator */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              {steps.map((_, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: idx === step ? "#8FBDC4" : "#E0E0E0",
                    transition: "all 0.3s ease",
                    transform: idx === step ? "scale(1.1)" : "scale(1)",
                    boxShadow:
                      idx === step ? "0 0 0 2px rgba(143,189,196,0.3)" : "none",
                  }}
                  aria-label={`Step ${idx + 1} of ${steps.length}`}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                mt: 1,
              }}
            >
              <IconButton
                onClick={handleBack}
                disabled={step === 0}
                sx={{
                  visibility: step === 0 ? "hidden" : "visible",
                  minWidth: 44,
                  minHeight: 44,
                  p: 1,
                  mr: 1.5,
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
                aria-label="Back"
                size="large"
                title="Back"
              >
                <ChevronLeftIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <Box sx={{ flex: 1 }} />
              <IconButton
                onClick={handleNext}
                disabled={step === steps.length - 1}
                sx={{
                  visibility: step === steps.length - 1 ? "hidden" : "visible",
                  minWidth: 44,
                  minHeight: 44,
                  p: 1,
                  ml: 1.5,
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
                aria-label="Next"
                size="large"
                title="Next"
              >
                <ChevronRightIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          </Box>
        </Fade>
      </DialogContent>
    </Dialog>
  );
};

export default HowToPlayModal;
